import type { IFaqResponse } from "@gik/api/acf/gik-settings/faq";
import { useFaq } from "@gik/api/acf/gik-settings/faq";
import { FetchProvider } from "@gik/core/components/DynamicComponent/FetchProvider";
import { InViewport } from "@gik/core/components/InViewport";
import type { WordpressFAQItem } from "@gik/core/models/wordpress/WordpressFAQItem";
import { ErrorPageSection } from "@gik/core/pages/Error/ErrorPageSection";
import type { UIComponent } from "@gik/core/types/UI";
import bemBlock from "@gik/core/utils/bemBlock";
import withComponentErrorBoundary from "@gik/core/utils/withComponentErrorBoundary";
import { CreateClickedLocation } from "@gik/create/enums/CreateAnalyticsEnums";
import { FAQStructuredData } from "@gik/seo/components/StructuredData";
import type { AccordionItemType } from "@gik/ui-legacy/Accordion";
import { Accordion } from "@gik/ui-legacy/Accordion";
import { PageHeader } from "@gik/ui-legacy/gik/PageHeader";
import { PageSection } from "@gik/ui-legacy/gik/PageSection";
import { StartInkindCard } from "@gik/ui-legacy/gik/StartInkindCard";
import { TestimonialsCarouselSection } from "@gik/ui-legacy/gik/TestimonialsCarouselSection";
import { HTMLParser } from "@gik/ui-legacy/HTMLParser";
import type React from "react";
import { useTranslation } from "react-i18next";
import { MarketingNavSection } from "../../components/MarketingNav/MarketingNavSection";
import { translationKeys } from "./i18n/en";

function removeParagraph(title: string): string {
	title = title.trim();
	if (title.startsWith("<p>") && title.endsWith("</p>")) {
		title = title.substring("<p>".length, title.length - "</p>".length);
	}
	return title;
}

function createAccordionItems(
	faqItems: WordpressFAQItem[],
): AccordionItemType[] {
	return faqItems?.map((item) => ({
		title: HTMLParser({ rawHtml: removeParagraph(item.question) }),
		content: HTMLParser({ rawHtml: item.answer }),
	}));
}

export type CommonQuestionsPageServerProps = {
	faq: IFaqResponse;
};

export type CommonQuestionsPageProps = {
	fallbackData?: CommonQuestionsPageServerProps;
} & UIComponent;

export const CommonQuestionsPageBlockName = "common-questions-page";

function CommonQuestionsPageComp(): React.ReactElement {
	const bem = bemBlock(CommonQuestionsPageBlockName);
	const { t } = useTranslation();

	// ponytail: read FAQ from SWR (hydrated from the page's SWRConfig fallback) so the
	// JSON-LD structured data renders at SSR, instead of being gated behind FetchProvider's
	// client-side isSuccess. The FetchProvider below still drives the accordion loading UX.
	const { data: faqData } = useFaq();

	return (
		<FAQStructuredData faqItems={faqData?.faq}>
			<FetchProvider<IFaqResponse, { items: AccordionItemType[]; raw: WordpressFAQItem[] }>
				fetch={useFaq}
				transform={(data) => ({
					items: createAccordionItems(data?.faq),
					raw: data?.faq,
				})}
			>
				{({ transformed }) => (
					<article className={bem()}>
						<InViewport height={160}>
							<PageHeader variant="sage" decoration="foliage">
								<h1>{t(translationKeys.title)}</h1>
							</PageHeader>
						</InViewport>

						<InViewport height={185}>
							<PageSection className={bem("lead")} noPadBottom>
								<p className="gik-section-lead">
									{t(translationKeys.leadDescription)}
								</p>
							</PageSection>
						</InViewport>

						<InViewport height={1634}>
							<PageSection>
								<Accordion items={transformed?.items} headingLevel={2} dividers />
							</PageSection>
						</InViewport>

						<InViewport height={175}>
							<StartInkindCard trackingId={CreateClickedLocation.FAQ_PAGE} />
						</InViewport>

						<InViewport height={394}>
							<TestimonialsCarouselSection />
						</InViewport>

						<InViewport height={461}>
							<MarketingNavSection variant="sage" />
						</InViewport>
					</article>
				)}
			</FetchProvider>
		</FAQStructuredData>
	);
}

export const CommonQuestionsPage = withComponentErrorBoundary(
	CommonQuestionsPageComp,
	{
		ErrorFallback: ErrorPageSection,
	},
);
